import Papa from 'papaparse';

/**
 * CSV conversion is deliberately a thin adapter over Papa Parse.  The
 * library handles quoted delimiters, doubled quotes and embedded newlines;
 * this module owns only Kivoza's limits, option validation and Markdown table
 * rendering.
 */
export const CSV_MAX_BYTES = 20 * 1024 * 1024;
export const CSV_MAX_ROWS = 20_000;
export const CSV_MAX_COLUMNS = 256;

function abortIfNeeded(signal) {
  if (signal?.aborted) throw new DOMException('Conversion canceled.', 'AbortError');
}

function validateDelimiter(delimiter) {
  const value = String(delimiter ?? ',');
  if ([...value].length !== 1 || value === '"' || value === '\r' || value === '\n') {
    throw new Error('Delimiter must be one character and cannot be a quote or line break.');
  }
  return value;
}

function parserErrorMessage(error) {
  const row = Number.isFinite(error?.row) ? ` on row ${error.row + 1}` : '';
  if (error?.code === 'MissingQuotes') return `Invalid CSV${row}: an opening quote has no closing quote.`;
  if (error?.code === 'TooManyFields') return `Invalid CSV${row}: this row has more fields than the header.`;
  if (error?.code === 'TooFewFields') return `Invalid CSV${row}: this row has fewer fields than the header.`;
  return `Invalid CSV${row}: ${error?.message || error?.code || 'the parser rejected the input.'}`;
}

// Papa Parse reports unterminated and malformed quoted fields, but accepts a
// quote embedded in an otherwise unquoted field. Validate only quote
// placement here; Papa remains responsible for splitting and decoding rows.
function validateQuoteSyntax(text, separator) {
  let inQuoted = false;
  let afterQuote = false;
  let fieldStart = true;
  let row = 1;
  let quotedRow = 1;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (inQuoted) {
      if (char === '"') {
        if (text[index + 1] === '"') {
          index += 1;
        } else {
          inQuoted = false;
          afterQuote = true;
        }
      } else if (char === '\r' || char === '\n') {
        if (char === '\r' && text[index + 1] === '\n') index += 1;
        row += 1;
      }
      continue;
    }
    if (afterQuote) {
      if (char === separator) {
        afterQuote = false;
        fieldStart = true;
      } else if (char === '\r' || char === '\n') {
        if (char === '\r' && text[index + 1] === '\n') index += 1;
        row += 1;
        afterQuote = false;
        fieldStart = true;
      } else {
        throw new Error(`Invalid CSV on row ${row}: a quoted field must end before the delimiter or line break.`);
      }
      continue;
    }
    if (char === '"') {
      if (!fieldStart) throw new Error(`Invalid CSV on row ${row}: a quote must start at the beginning of a field.`);
      inQuoted = true;
      quotedRow = row;
      fieldStart = false;
    } else if (char === separator) {
      fieldStart = true;
    } else if (char === '\r' || char === '\n') {
      if (char === '\r' && text[index + 1] === '\n') index += 1;
      row += 1;
      fieldStart = true;
    } else {
      fieldStart = false;
    }
  }
  if (inQuoted) throw new Error(`Invalid CSV on row ${quotedRow}: an opening quote has no closing quote.`);
}

/** @returns {string[][]} */
export function parseCsv(source, { delimiter = ',', signal } = {}) {
  const text = String(source ?? '').replace(/^\uFEFF/, '');
  const separator = validateDelimiter(delimiter);
  if (new TextEncoder().encode(text).byteLength > CSV_MAX_BYTES) throw new Error('The CSV source is larger than 20 MB.');
  if (!text.trim()) throw new Error('The CSV source is empty.');
  abortIfNeeded(signal);
  validateQuoteSyntax(text, separator);
  const result = Papa.parse(text, {
    delimiter: separator,
    header: false,
    skipEmptyLines: false,
    quoteChar: '"',
    escapeChar: '"',
    fastMode: false,
  });
  abortIfNeeded(signal);
  const errors = Array.isArray(result.errors) ? result.errors : [];
  if (errors.length) throw new Error(parserErrorMessage(errors[0]));
  const rows = Array.isArray(result.data)
    ? result.data.map((row) => Array.isArray(row) ? row.map((value) => String(value ?? '')) : [String(row ?? '')])
    : [];
  while (rows.length && rows.at(-1).length === 1 && rows.at(-1)[0] === '') rows.pop();
  if (!rows.length) throw new Error('The CSV source is empty.');
  if (rows.length > CSV_MAX_ROWS) throw new Error(`CSV has more than ${CSV_MAX_ROWS.toLocaleString()} rows.`);
  const width = Math.max(...rows.map((row) => row.length));
  if (width > CSV_MAX_COLUMNS) throw new Error(`CSV has more than ${CSV_MAX_COLUMNS} columns.`);
  rows.forEach((row) => { while (row.length < width) row.push(''); });
  return rows;
}

function markdownCell(value) {
  return String(value ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/\|/g, '\\|')
    .replace(/\r\n?|\n/g, '<br>');
}

function uniqueHeaders(row, width) {
  const used = new Map();
  return Array.from({ length: width }, (_, index) => {
    const original = String(row[index] ?? '').trim() || `Column ${index + 1}`;
    const count = (used.get(original) || 0) + 1;
    used.set(original, count);
    return count === 1 ? original : `${original} (${count})`;
  });
}

/** @returns {{markdown: string, warnings: string[], assets: [], rows: string[][]}} */
export function convertCsvToMarkdown(source, {
  delimiter = ',',
  hasHeader = true,
  maxRows = 10_000,
  trim = true,
  signal,
} = {}) {
  const rowLimit = Number(maxRows);
  if (!Number.isInteger(rowLimit) || rowLimit < 1 || rowLimit > CSV_MAX_ROWS) throw new Error(`Maximum data rows must be between 1 and ${CSV_MAX_ROWS.toLocaleString()}.`);
  const rows = parseCsv(source, { delimiter, signal });
  abortIfNeeded(signal);
  const width = Math.max(...rows.map((row) => row.length));
  const warnings = [];
  const originalWidths = rows.map((row) => row.length);
  if (new Set(originalWidths).size > 1) warnings.push('Rows had different field counts; missing cells were padded as empty values.');
  const header = hasHeader ? uniqueHeaders(rows[0], width) : Array.from({ length: width }, (_, index) => `Column ${index + 1}`);
  let data = hasHeader ? rows.slice(1) : rows;
  if (trim) data = data.map((row) => row.map((value) => value.trim()));
  if (data.length > rowLimit) {
    data = data.slice(0, rowLimit);
    warnings.push(`Rows beyond the ${rowLimit.toLocaleString()} row limit were omitted.`);
  }
  const lines = [
    `| ${header.map(markdownCell).join(' | ')} |`,
    `| ${header.map(() => '---').join(' | ')} |`,
    ...data.map((row) => `| ${Array.from({ length: width }, (_, index) => markdownCell(row[index])).join(' | ')} |`),
  ];
  const markdown = lines.join('\n');
  if (!data.length) warnings.push('The CSV contains a header row but no data rows.');
  return { markdown, warnings, assets: [], rows };
}

export function validateCsvText(source, options = {}) {
  try {
    parseCsv(source, options);
    return '';
  } catch (error) {
    return error instanceof Error ? error.message : 'Invalid CSV.';
  }
}

/** Cheap UI validation; parsing stays inside the cancellable Worker. */
export function validateCsvInput(source, { delimiter = ',', maxRows = 10_000 } = {}) {
  const text = String(source ?? '');
  if (!text.trim()) return 'Paste CSV or choose a CSV file first.';
  if (new TextEncoder().encode(text).byteLength > CSV_MAX_BYTES) return 'The CSV source is larger than 20 MB.';
  try { validateDelimiter(delimiter); } catch (error) { return error instanceof Error ? error.message : 'Invalid CSV delimiter.'; }
  const rowLimit = Number(maxRows);
  if (!Number.isInteger(rowLimit) || rowLimit < 1 || rowLimit > CSV_MAX_ROWS) return `Maximum data rows must be between 1 and ${CSV_MAX_ROWS.toLocaleString()}.`;
  return '';
}
